import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo } from 'react';
import { useEvent } from '../hooks/useEvent';
import { getSelectedRows } from '../utils/getSelectedRows';
import { reorderByFieldName } from './helpers';
const tableContext = createContext(undefined);
const typeSmartTableContext = (context) => context;
export function SmartTableContext({ table, children }) {
    const Provider = typeSmartTableContext(tableContext).Provider;
    return _jsx(Provider, { value: table, children: children });
}
const useSTContextOrThrow = (hookName) => {
    const context = useContext(typeSmartTableContext(tableContext));
    if (!context) {
        throw new Error(`${hookName} has to be called inside of SmartTableContext`);
    }
    return context;
};
export const useSTRowFilterCondition = () => {
    const context = useSTContextOrThrow('useSTRows');
    return useMemo(() => [context.rowFilterCondition, context.setRowFilterCondition], [context.rowFilterCondition, context.setRowFilterCondition]);
};
export const useSTContext = () => useSTContextOrThrow('useSTContext');
export const useSTRows = () => {
    const context = useSTContextOrThrow('useSTRows');
    return useMemo(() => ({
        selectedRows: getSelectedRows(context.rows, context.isSelected),
        rows: context.rows,
        rowsLoading: context.rowsLoading,
        loadRowsRange: context.loadRange,
        rowsCount: context.totalRows,
        rowsFilterCondition: context.rowFilterCondition,
        setRowsFilterCondition: context.setRowFilterCondition,
        rowsSelectEnabled: context.rowsSelectEnabled,
        allSelected: context.allSelected,
        selectionCount: context.selectionCount,
        deselectAll: context.deselectAll,
        selectAll: context.selectAll,
    }), [
        context.rowsLoading,
        context.isSelected,
        context.allSelected,
        context.deselectAll,
        context.loadRange,
        context.rowFilterCondition,
        context.rows,
        context.rowsSelectEnabled,
        context.selectionCount,
        context.setRowFilterCondition,
        context.totalRows,
        context.selectAll,
    ]);
};
export const useSTRow = (row) => {
    const context = useSTContextOrThrow('useSTRow');
    return useMemo(() => ({
        isSelected: context.isSelected(row),
        toggleSelected: () => context.toggleSelected(row),
    }), [context, row]);
};
export const useSTColumns = () => {
    const context = useSTContextOrThrow('useSTColumns');
    return useMemo(() => ({
        allColumns: context.allColumns,
        visibleColumns: context.visibleColumns,
        isColumnVisible: context.isColumnVisible,
        setColumnVisibility: context.setColumnVisibility,
        toggleColumnVisibility: context.toggleColumnVisibility,
        setAllColumnsVisible: context.setAllColumnsVisible,
        setAllColumnsHidden: context.setAllColumnsHidden,
        columnOrder: context.columnOrder,
        setColumnOrder: context.setColumnOrder,
        sorting: context.sorting,
        clearSorting: context.clearSorting,
    }), [
        context.allColumns,
        context.visibleColumns,
        context.isColumnVisible,
        context.setColumnVisibility,
        context.toggleColumnVisibility,
        context.setAllColumnsVisible,
        context.setAllColumnsHidden,
        context.columnOrder,
        context.setColumnOrder,
        context.sorting,
        context.clearSorting,
    ]);
};
export const useSTColumn = (column) => {
    const context = useSTContextOrThrow('useSTColumn');
    const index = context.sorting.findIndex((sortInfo) => sortInfo.fieldName === column.fieldName);
    const isOnlySorting = context.sorting.length === 1 && context.sorting[0].fieldName === column.fieldName;
    return useMemo(() => ({
        isOnlySorting,
        isColumnVisible: context.isColumnVisible(column),
        setColumnVisibility: (visibility) => context.setColumnVisibility(column, visibility),
        toggleColumnVisibility: () => context.toggleColumnVisibility(column),
        toggleSorting: () => context.toggleSorting(column.fieldName),
        removeSorting: () => context.removeColumnSorting(column.fieldName),
        setAsOnlySorting: (direction) => context.setAsOnlySorting(column.fieldName, direction),
        setSorting: (direction) => context.setColumnSorting(column.fieldName, direction),
        columnSortingIndex: index,
        columnSortingInfo: index === -1 ? null : context.sorting[index],
    }), [column, context, index, isOnlySorting]);
};
export const useSTColumnOrder = () => {
    const { columnOrder, setColumnOrder, visibleColumns, allColumns, setAllColumnsVisible } = useSTColumns();
    const handleShiftColumns = useEvent(() => {
        const visibleColumnsNames = visibleColumns.map((column) => column.fieldName);
        const hiddenColumnsNames = allColumns
            .map((column) => column.fieldName)
            .filter((fieldName) => !visibleColumnsNames.includes(fieldName));
        const shiftedColumns = [...visibleColumnsNames, ...hiddenColumnsNames];
        setColumnOrder(shiftedColumns);
    });
    const handleMoveColumn = useEvent((fromFieldName, toFieldName) => setColumnOrder((prev) => reorderByFieldName(fromFieldName, toFieldName, prev)));
    const handleResetColumnOrder = useEvent(() => {
        // Reset columnOrder - nastavit na undefined, aby se použila výchozí hodnota z fallbacku
        // (což je pořadí podle definice sloupců z API při inicializaci)
        setColumnOrder(() => []);
        // Reset columnsVisibility na výchozí hodnotu (prázdný objekt = všechny viditelné kromě defaultHiddenColumns)
        setAllColumnsVisible();
    });
    return useMemo(() => ({
        columnOrder,
        setColumnOrder,
        shiftColumns: handleShiftColumns,
        moveColumn: handleMoveColumn,
        resetColumnOrder: handleResetColumnOrder,
    }), [columnOrder, handleMoveColumn, handleResetColumnOrder, handleShiftColumns, setColumnOrder]);
};
export const useSTApi = () => {
    const context = useSTContextOrThrow('useSTApi');
    return useMemo(() => context.api, [context.api]);
};
export const useSTReload = () => {
    const context = useSTContextOrThrow('useSTReload');
    return useMemo(() => ({
        reloadColumns: context.reloadColumns,
        resetRows: context.resetRows,
        invalidateTable: context.invalidateTable,
    }), [context.reloadColumns, context.resetRows, context.invalidateTable]);
};
//# sourceMappingURL=SmartTableContext.js.map